type SearchMiddlewareContext<TSearchSchema> = {
  search: TSearchSchema;
  next: (newSearch: TSearchSchema) => TSearchSchema;
};

type SearchMiddleware<TSearchSchema> = (
  ctx: SearchMiddlewareContext<TSearchSchema>,
) => TSearchSchema;

/**
 * Creates a search middleware that strips unknown fields not defined in the reference object.
 *
 * @param referenceObject - An object containing the valid keys (typically from schema.parse({}))
 * @returns A search middleware that removes unknown keys
 *
 * @example
 * ```ts
 * const searchSchema = z.object({
 *   page: z.number().default(1),
 *   filter: z.string().default('')
 * });
 *
 * export const Route = createFileRoute('/example')({
 *   validateSearch: searchSchema,
 *   search: {
 *     middlewares: [stripUnknownSearchParams(searchSchema.parse({}))]
 *   }
 * });
 * ```
 */
export function stripUnknownSearchParams<
  TSearchSchema extends Record<string, unknown>,
>(referenceObject: TSearchSchema): SearchMiddleware<Partial<TSearchSchema>> {
  return ({
    search,
    next,
  }: SearchMiddlewareContext<Partial<TSearchSchema>>) => {
    const result = next(search);

    // Get valid keys from the reference object
    const validKeys = Object.keys(referenceObject);

    // Filter result to only include valid keys
    const filtered: Partial<TSearchSchema> = {};
    for (const key of validKeys) {
      if (key in result) {
        filtered[key as keyof TSearchSchema] =
          result[key as keyof TSearchSchema];
      }
    }

    return filtered;
  };
}
